import { useEffect, useRef, useState } from 'react'

// One filter control, used by every screen that lists vendors.
//
// # WHY IT IS A POPOVER AND NOT A PANEL
//
// The panel it replaces was five rows of chips, always open, taking a third of
// the screen before a single vendor was visible. Filtering is something
// somebody does for ten seconds and then reads the result for ten minutes, so
// the choosing belongs behind a button and the result belongs on the screen.
//
// What stays visible is what is currently applied, as removable chips. A
// filter you cannot see is a filter you argue with: "the register says four
// vendors" is only true until somebody forgets that Tier 1 is still ticked.

export type FilterOption = { value: string; label: string }
export type FilterGroup = { key: string; label: string; options: FilterOption[] }

export type FilterValue = Record<string, string[]>

function count(v: FilterValue): number {
  return Object.values(v).reduce((n, xs) => n + xs.length, 0)
}

export function FilterBar({
  groups,
  value,
  onChange,
  children,
  panelExtra,
}: {
  groups: FilterGroup[]
  value: FilterValue
  onChange: (v: FilterValue) => void
  // Anything the screen wants beside the control: a count, a link, a button.
  children?: React.ReactNode
  // Shown at the foot of the open panel: the screen's own way of choosing,
  // for a screen that has one.
  panelExtra?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const wrap = useRef<HTMLDivElement>(null)
  const applied = count(value)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  const toggle = (g: string, v: string) => {
    const cur = value[g] ?? []
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]
    onChange({ ...value, [g]: next })
  }

  const chips: { group: string; option: FilterOption }[] = []
  for (const g of groups) {
    for (const v of value[g.key] ?? []) {
      const o = g.options.find((x) => x.value === v)
      if (o) chips.push({ group: g.key, option: o })
    }
  }

  return (
    <div className="filterbar" ref={wrap}>
      <div className="filterbar-row">
        <button
          type="button"
          className={'filterbar-open' + (applied > 0 ? ' on' : '')}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          Filters{applied > 0 ? ' (' + applied + ')' : ''}
        </button>

        {chips.map(({ group, option }) => (
          <button
            type="button"
            className="filterbar-chip"
            key={group + option.value}
            onClick={() => toggle(group, option.value)}
            title={'Remove ' + option.label}
          >
            {option.label}
            <span aria-hidden="true">×</span>
          </button>
        ))}

        {applied > 0 && (
          <button type="button" className="filterbar-clear" onClick={() => onChange({})}>
            Clear all
          </button>
        )}

        <span className="filterbar-spacer" />
        {children}
      </div>

      {open && (
        <div className="filterbar-panel" role="dialog" aria-label="Filters">
          <div className="filterbar-groups">
            {groups.map((g) => (
              <div className="filterbar-group" key={g.key}>
                <h3>{g.label}</h3>
                <div className="filterbar-options">
                  {g.options.map((o) => {
                    const on = (value[g.key] ?? []).includes(o.value)
                    return (
                      <button
                        type="button"
                        key={o.value}
                        className={'filterbar-option' + (on ? ' on' : '')}
                        aria-pressed={on}
                        onClick={() => toggle(g.key, o.value)}
                      >
                        {o.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          {panelExtra && <div className="filterbar-extra">{panelExtra}</div>}
          <div className="filterbar-foot">
            <button type="button" className="ghost" onClick={() => onChange({})}>
              Clear
            </button>
            <button type="button" className="primary" onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
